import React, { useEffect, useRef, useState } from 'react';
import { motion, animate } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * BenchmarkBar — Horizontal bar for comparing measured values
 * Each bar watches itself: when it scrolls into view the fill grows and the number counts up,
 * then a single sheen passes over the fill. Reduced motion shows the final state at once.
 *
 * Importers: src/pages/GoingMerryPage.jsx (via BenchmarkMatrix)
 * Data schema: Props accept value (number), max (number), label (string), unit (string),
 * color ('ice'|'goingmerry'|'ramsey'|'cortex'), showValue (boolean), delay (seconds), className, style.
 * The bar's length is value/max; the number shown is the measured value itself.
 */
const COLORS = {
  ice: 'var(--memory-blue)',
  goingmerry: 'var(--velocity-cyan)',
  ramsey: 'var(--truth-amber)',
  cortex: 'var(--orchestration-violet)',
};

const EASE = [0.16, 1, 0.3, 1];
const FILL_SECONDS = 1.4;

const format = (n) => String(Number(n.toFixed(2)));

const BenchmarkBar = ({
  value = 0,
  max = 100,
  label,
  unit = '',
  color = 'goingmerry',
  showValue = true,
  delay = 0,
  className = '',
  style,
  // Accepted for compatibility with older callers; the bar no longer shares scroll-beat ids.
  // eslint-disable-next-line no-unused-vars
  beatId,
}) => {
  const reducedMotion = useReducedMotion();
  const rowRef = useRef(null);
  const [state, setState] = useState('idle'); // idle -> filling -> done
  const [shown, setShown] = useState(0);
  const barColor = COLORS[color] || COLORS.goingmerry;
  const target = max > 0 ? Math.min(value / max, 1) : 0;

  // Start once, when the row first scrolls into view
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setState((s) => (s === 'idle' ? (reducedMotion ? 'done' : 'filling') : s));
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  // Count the number up in step with the fill
  useEffect(() => {
    if (state === 'done') {
      setShown(value);
      return undefined;
    }
    if (state !== 'filling') return undefined;
    const controls = animate(0, value, {
      duration: FILL_SECONDS,
      delay,
      ease: EASE,
      onUpdate: setShown,
      onComplete: () => setState('done'),
    });
    return () => controls.stop();
  }, [state, value, delay]);

  const scale = state === 'idle' ? 0 : target;

  return (
    <div ref={rowRef} className={`benchmark-bar ${className}`} style={{ width: '100%', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <span
          style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)', flex: '0 0 9rem', textAlign: 'left' }}
        >
          {label}
        </span>

        <div
          aria-hidden="true"
          style={{
            flex: '1 1 12rem',
            position: 'relative',
            height: '12px',
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        >
          <motion.div
            data-testid="benchmark-fill"
            data-state={state}
            initial={false}
            animate={{ scaleX: scale }}
            transition={reducedMotion ? { duration: 0 } : { duration: FILL_SECONDS, delay, ease: EASE }}
            style={{
              position: 'absolute',
              inset: 0,
              transformOrigin: 'left center',
              overflow: 'hidden', // keeps the sheen inside the fill
              background: `linear-gradient(90deg, ${barColor}, color-mix(in srgb, ${barColor} 80%, transparent))`,
              borderRadius: '6px',
              boxShadow: `0 0 16px color-mix(in srgb, ${barColor} 25%, transparent)`,
            }}
          >
            {state === 'done' && !reducedMotion && (
              <motion.span
                initial={{ x: '-120%' }}
                animate={{ x: '320%' }}
                transition={{ duration: 0.9, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '30%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                }}
              />
            )}
          </motion.div>
        </div>

        {showValue && (
          <>
            {/* Assistive technology and crawlers read the real value; sighted users watch it count up */}
            <span className="sr-only">
              {format(value)}
              {unit}
            </span>
            <span
              aria-hidden="true"
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                fontVariantNumeric: 'tabular-nums',
                color: barColor,
                minWidth: '6rem',
                textAlign: 'right',
              }}
            >
              {format(shown)}
              {unit}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * BenchmarkMatrix — a stack of bars that share one scale; each reveals itself, a beat apart
 */
export const BenchmarkMatrix = ({ rows = [], maxValue, className = '', style, beatId, ...props }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const globalMax = maxValue || Math.max(...safeRows.map((r) => r.max || r.value || 0), 1);

  return (
    <div className={`benchmark-matrix ${className}`} data-beat-id={beatId} style={{ display: 'grid', gap: '1.25rem', ...style }} {...props}>
      {safeRows.map((row, index) => (
        <BenchmarkBar
          key={row.label || index}
          value={row.value}
          max={row.max || globalMax}
          label={row.label}
          unit={row.unit || ''}
          color={row.color || 'goingmerry'}
          showValue={row.showValue !== false}
          delay={index * 0.15}
        />
      ))}
    </div>
  );
};

export default BenchmarkBar;
